// src/pages/customer-panel/calendar/CalendarPage.js
// Calendar View Page

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Divider,
    Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import EventIcon from '@mui/icons-material/Event';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const EVENT_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

export default function CalendarPage() {
    const today = new Date();
    const [currentMonth, setCurrentMonth] = useState(today.getMonth());
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get('/calendar/events', {
                params: { month: currentMonth + 1, year: currentYear },
            });
            const data = response.data;
            setEvents(data.data || data.events || []);
        } catch (err) {
            console.error('Failed to fetch calendar events:', err);
            setError(err.response?.data?.message || 'Failed to load calendar events.');
        } finally {
            setLoading(false);
        }
    }, [currentMonth, currentYear]);

    useEffect(() => {
        document.title = 'Calendar | Customer Panel';
        fetchEvents();
    }, [fetchEvents]);

    const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

    const handlePrevMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear((prev) => prev - 1);
        } else {
            setCurrentMonth((prev) => prev - 1);
        }
    };

    const handleNextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear((prev) => prev + 1);
        } else {
            setCurrentMonth((prev) => prev + 1);
        }
    };

    const handleToday = () => {
        setCurrentMonth(today.getMonth());
        setCurrentYear(today.getFullYear());
    };

    const getEventsForDate = (day) => {
        return events.filter((event) => {
            const eventDate = new Date(event.date || event.start_date || event.startDate);
            return (
                eventDate.getDate() === day &&
                eventDate.getMonth() === currentMonth &&
                eventDate.getFullYear() === currentYear
            );
        });
    };

    const handleDateClick = (day) => {
        const dateEvents = getEventsForDate(day);
        setSelectedDate({
            day,
            month: currentMonth,
            year: currentYear,
            events: dateEvents,
        });
        setDialogOpen(true);
    };

    const isToday = (day) =>
        day === today.getDate() &&
        currentMonth === today.getMonth() &&
        currentYear === today.getFullYear();

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return '';
        }
    };

    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);

    // Build calendar grid
    const calendarDays = [];
    // Empty slots before first day
    for (let i = 0; i < firstDay; i++) {
        calendarDays.push(null);
    }
    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
        calendarDays.push(day);
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        Calendar
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                        View your events and schedule
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchEvents} sx={{ color: '#64748b' }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        startIcon={<TodayIcon />}
                        variant="outlined"
                        onClick={handleToday}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            borderColor: GREEN_ACCENT,
                            color: GREEN_ACCENT,
                            borderRadius: '10px',
                            textTransform: 'none',
                            '&:hover': { borderColor: '#059669', bgcolor: `${GREEN_ACCENT}08` },
                        }}
                    >
                        Today
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>
                    {error}
                </Alert>
            )}

            <Paper
                sx={{
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                }}
            >
                {/* Month Navigation */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        py: 2,
                        borderBottom: '1px solid',
                        borderColor: 'grey.100',
                        bgcolor: '#f8fafc',
                    }}
                >
                    <IconButton onClick={handlePrevMonth} sx={{ color: '#64748b' }}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                        {MONTHS[currentMonth]} {currentYear}
                    </Typography>
                    <IconButton onClick={handleNextMonth} sx={{ color: '#64748b' }}>
                        <ChevronRightIcon />
                    </IconButton>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress size={32} sx={{ color: GREEN_ACCENT }} />
                    </Box>
                ) : (
                    <Box sx={{ p: { xs: 1, md: 2 } }}>
                        {/* Weekday Headers */}
                        <Grid container>
                            {WEEKDAYS.map((day) => (
                                <Grid item xs key={day} sx={{ textAlign: 'center', py: 1.5 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontFamily,
                                            fontWeight: 700,
                                            color: '#94a3b8',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        {day}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Calendar Grid */}
                        <Grid container>
                            {calendarDays.map((day, index) => {
                                if (day === null) {
                                    return (
                                        <Grid
                                            item
                                            xs
                                            key={`empty-${index}`}
                                            sx={{
                                                minHeight: { xs: 60, md: 90 },
                                                borderTop: '1px solid',
                                                borderColor: 'grey.100',
                                            }}
                                        />
                                    );
                                }

                                const dayEvents = getEventsForDate(day);
                                const isTodayDate = isToday(day);

                                return (
                                    <Grid
                                        item
                                        xs
                                        key={`day-${day}`}
                                        onClick={() => handleDateClick(day)}
                                        sx={{
                                            minHeight: { xs: 60, md: 90 },
                                            borderTop: '1px solid',
                                            borderColor: 'grey.100',
                                            cursor: 'pointer',
                                            p: 0.5,
                                            transition: 'background-color 0.15s',
                                            '&:hover': { bgcolor: '#f8fafc' },
                                            position: 'relative',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                width: 28,
                                                height: 28,
                                                borderRadius: '50%',
                                                mx: 'auto',
                                                mb: 0.5,
                                                bgcolor: isTodayDate ? GREEN_ACCENT : 'transparent',
                                                color: isTodayDate ? 'white' : '#1e293b',
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily,
                                                    fontWeight: isTodayDate ? 700 : 500,
                                                    fontSize: '0.8rem',
                                                }}
                                            >
                                                {day}
                                            </Typography>
                                        </Box>
                                        {/* Event indicators */}
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, justifyContent: 'center' }}>
                                            {dayEvents.slice(0, 3).map((event, ei) => (
                                                <Box
                                                    key={event.id || event._id || ei}
                                                    sx={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        bgcolor: event.color || EVENT_COLORS[ei % EVENT_COLORS.length],
                                                    }}
                                                />
                                            ))}
                                            {dayEvents.length > 3 && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{ fontFamily, fontSize: '0.6rem', color: '#94a3b8', lineHeight: 1 }}
                                                >
                                                    +{dayEvents.length - 3}
                                                </Typography>
                                            )}
                                        </Box>
                                        {/* Show first event title on larger screens */}
                                        {dayEvents.length > 0 && (
                                            <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 0.5, px: 0.5 }}>
                                                {dayEvents.slice(0, 2).map((event, ei) => (
                                                    <Chip
                                                        key={event.id || event._id || ei}
                                                        label={event.title || event.name || 'Event'}
                                                        size="small"
                                                        sx={{
                                                            fontFamily,
                                                            fontSize: '0.6rem',
                                                            height: 18,
                                                            mb: 0.3,
                                                            maxWidth: '100%',
                                                            bgcolor: `${event.color || EVENT_COLORS[ei % EVENT_COLORS.length]}20`,
                                                            color: event.color || EVENT_COLORS[ei % EVENT_COLORS.length],
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': {
                                                                px: 0.8,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                )}
            </Paper>

            {/* Date Events Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventIcon sx={{ color: GREEN_ACCENT }} />
                        {selectedDate &&
                            `${MONTHS[selectedDate.month]} ${selectedDate.day}, ${selectedDate.year}`}
                    </Box>
                    <IconButton onClick={() => setDialogOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedDate?.events?.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <EventIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>
                                No events on this day
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            {selectedDate?.events?.map((event, index) => (
                                <Paper
                                    key={event.id || event._id || index}
                                    sx={{
                                        p: 2,
                                        borderRadius: '12px',
                                        border: '1px solid',
                                        borderColor: 'grey.100',
                                        borderLeft: `4px solid ${event.color || EVENT_COLORS[index % EVENT_COLORS.length]}`,
                                    }}
                                >
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                                        {event.title || event.name || 'Event'}
                                    </Typography>
                                    {event.description && (
                                        <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mb: 1 }}>
                                            {event.description}
                                        </Typography>
                                    )}
                                    {(event.start_time || event.startTime || event.time) && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccessTimeIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                                {formatTime(event.start_time || event.startTime || event.time)}
                                                {(event.end_time || event.endTime) &&
                                                    ` - ${formatTime(event.end_time || event.endTime)}`}
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
